import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { EN_TO_FR, FR_TO_EN } from "../utils/langRoutes";
import { isProductionHost } from "../utils/isProductionHost";

const DEFAULT_TITLE = "Visitor to Canada Insurance Comparison Tool | einsured.ca";
const DEFAULT_DESCRIPTION =
  "Compare visitor to Canada insurance plans with einsured.ca. Explore medical coverage, deductibles, pre-existing conditions, & policy limits to compare options.";

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage,
  noIndex = false,
}) => {
  const location = useLocation();

  const CANONICAL_ORIGIN = "https://www.einsured.ca";

  // Staging/preview deployments (staging.einsured.ca, squaretrigger preview,
  // etc.) run this exact same build. Without this, they'd be fully
  // indexable — same robots.txt, same schema, same everything as
  // production. Force noindex and drop structured data anywhere that isn't
  // the real production host, regardless of what each page passes in.
  const isNonProductionHost = !isProductionHost();

  const canonicalUrl = useMemo(() => {
    const path = location.pathname === "/" ? "" : location.pathname;
    return `${CANONICAL_ORIGIN}${path}`.replace(/\/+$/, "");
  }, [location.pathname]);

  const isFrench =
    location.pathname === "/fr" || location.pathname.startsWith("/fr/");

  const { enPath, frPath } = useMemo(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";

    if (isFrench) {
      return { enPath: FR_TO_EN[path] ?? "/", frPath: path };
    }

    // Blog is English-only, so blog pages have no French alternate to advertise.
    const isBlog = path === "/blog" || path.startsWith("/blog/");
    return { enPath: path, frPath: isBlog ? null : (EN_TO_FR[path] ?? "/fr") };
  }, [location.pathname, isFrench]);

  const enUrl = `${CANONICAL_ORIGIN}${enPath}`.replace(/\/+$/, "") || CANONICAL_ORIGIN;
  const frUrl = frPath ? `${CANONICAL_ORIGIN}${frPath}` : null;

  const safeTitle = (title ?? DEFAULT_TITLE).trim() || DEFAULT_TITLE;
  const safeDescription =
    (description ?? DEFAULT_DESCRIPTION).trim() || DEFAULT_DESCRIPTION;

  const finalOgTitle = (ogTitle || safeTitle).trim();
  const finalOgDesc = (ogDescription || safeDescription).trim();

  const resolvedOgImage = useMemo(() => {
    if (!ogImage) return "";
    const s = String(ogImage).trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (s.startsWith("/")) return `${CANONICAL_ORIGIN}${s}`;
    return s;
  }, [ogImage]);

  const websiteSchema = {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "einsured.ca",
    url: "https://www.einsured.ca/",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.einsured.ca/blog?s={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "einsured.ca",
    url: "https://www.einsured.ca/",
    logo: "https://www.einsured.ca/fav.png",
    sameAs: [
      "https://www.facebook.com/einsured.ca/",
      "https://www.linkedin.com/company/einsured-ca/",
      "https://www.instagram.com/einsured.ca/",
      "https://x.com/Einsured_ca",
      "https://maps.app.goo.gl/uQwSfgJrR1ZrayLR7",
    ],
  };

  return (
    <Helmet prioritizeSeoTags>
      <html lang={isFrench ? "fr" : "en"} />
      <title>{safeTitle}</title>

      <meta name="description" content={safeDescription} />
      <meta
        name="robots"
        content={
          noIndex || isNonProductionHost ? "noindex, nofollow" : "index, follow"
        }
      />
      {keywords ? <meta name="keywords" content={String(keywords)} /> : null}

      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang alternates */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      {frUrl ? <link rel="alternate" hrefLang="fr" href={frUrl} /> : null}
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      {resolvedOgImage ? (
        <meta property="og:image" content={resolvedOgImage} />
      ) : null}

      {/* Twitter */}
      <meta
        name="twitter:card"
        content={resolvedOgImage ? "summary_large_image" : "summary"}
      />
      <meta name="twitter:site" content="@Einsured_ca" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDesc} />
      {resolvedOgImage ? (
        <meta name="twitter:image" content={resolvedOgImage} />
      ) : null}

      {/* Schema — omitted entirely on non-production hosts so staging/preview
          deployments never get attributed to the real business via rich results. */}
      {!isNonProductionHost && location.pathname === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      ) : null}

      {!isNonProductionHost ? (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default SEO;
